import React, { useRef, useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import ePub from "epubjs";

import {
  closeAnnotatorBar,
  openAnnotatorBar,
  setSelectedHighlight
} from "../../redux/highlightSlice";
import { selectDarkMode } from "../../redux/firestoreSelectors";
import { setSelectedTempHighlight } from "../../redux/interactionsSlice";
import { isPdfSelector, selectCurrentText } from "../../redux/textsSlice";

import { showInText } from "../../utils/showInTextHelper";
import { getHighlightColor } from "../../utils/colors";
import { getLocationByEntity } from "../reader/utils";

import ReactReaderActions from "../annotations/ReactReaderActions";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography
} from "@mui/material";
import PdfActionBar from "./NewAssignmentLayout/MainContent/ActionBar/PdfActionBar";
import PdfView from "../reader/pdf/PdfView";
import EpubView from "../reader/EpubView/EpubView";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const useStyles = makeStyles((theme) => ({
  dialog: {
    padding: 0,
    zIndex: 10
  },
  actionBarWrapper: {
    position: "sticky",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: "56px",
    width: "auto",
    paddingInline: "12px"
  },
  reader: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    overflow: "auto",
    minHeight: "500px",
    maxHeight: "calc(100vh - 200px)"
  }
}));

const HighlightsDialog = ({
  dialogOpen,
  setDialogOpen,
  addHighlight,
  removeHighlight,
  highlights = []
}) => {
  const classes = useStyles();
  // const intl = useIntl();
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const isAnnotatorBarOpen = useSelector(
    (state) => state.highlighter.isAnnotatorBarOpen
  );
  const fontSizeValue = useSelector((state) => state.user.userProfile.fontSize);
  const fontSize = useSelector((state) => state.user.fontSizeOptions);
  const isPdf = useSelector(isPdfSelector);
  const text = useSelector(selectCurrentText);
  const darkMode = useSelector((state) => selectDarkMode(state));
  const user_id = useSelector((state) => state.firebase.auth.uid);

  const [scrollTop, setScrollTop] = useState(false);
  const [rendition, setRendition] = useState(null);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(0);

  const EpubCFI = new ePub.CFI();
  const containerScrollTop =
    containerRef && containerRef.current && containerRef.current.scrollTop;

  useEffect(() => {
    if (
      scrollTop &&
      containerRef &&
      containerRef.current &&
      Math.abs(containerRef.current.scrollTop - scrollTop) > 2
    ) {
      containerRef.current.scrollTop = scrollTop;
    }
    if (
      containerRef &&
      containerRef.current &&
      scrollTop &&
      Math.abs(containerRef.current.scrollTop - scrollTop) < 2
    ) {
      setScrollTop(false);
    }
  }, [scrollTop, containerScrollTop]);

  const navigateToHighlight = (index) => {
    if (highlights.length === 0 || index < 0 || index >= highlights.length) {
      return;
    }
    const highlight = highlights[index];
    dispatch(setSelectedTempHighlight(highlight));
    setCurrentHighlightIndex(index);
    showInText(false, highlight, user_id, rendition);
  };

  const navigateToPreviousHighlight = () => {
    if (highlights.length === 0) return;

    const newIndex =
      currentHighlightIndex > 0
        ? currentHighlightIndex - 1
        : highlights.length - 1;

    navigateToHighlight(newIndex);
  };

  const navigateToNextHighlight = () => {
    if (highlights.length === 0) return;

    const newIndex =
      currentHighlightIndex < highlights.length - 1
        ? currentHighlightIndex + 1
        : 0;

    navigateToHighlight(newIndex);
  };

  const addHighlightToQuestion = (quote, shouldDelete = false) => {
    let adjustedHighlight = {
      ...quote,
      id: uuid(),
      source: "taskFindAnswer",
      color: getHighlightColor("#00B9FB", darkMode)
    };
    if (addHighlight || removeHighlight) {
      setScrollTop(0);
    }
    if (!shouldDelete) {
      addHighlight && addHighlight(adjustedHighlight);
    } else removeHighlight && removeHighlight(quote);
  };

  const onTextSelected = (params) => {
    console.log("Text selected:", params);

    if (params.shouldClose) {
      dispatch(closeAnnotatorBar());
    } else {
      addHighlightToQuestion(params.selection);
      let content = rendition && rendition.getContents()[0];

      if (content && content.window && content.window.getSelection()) {
        content.window.getSelection().empty();
      }
    }
  };

  const onRenditionCreated = (rendition) => {
    const gotoQuote = (rendition, target) => {
      let section = rendition.book.spine.get(target);
      var visible = section && rendition.manager.views.find(section); // View is already shown, just move to correct location in view

      if (target === section.href) {
        target = undefined;
      }
      if (visible && section) {
        let offset = target ? visible.locationOf(target) : visible.offset();
        let destTop = offset.top;

        setScrollTop(destTop);
      }
    };

    let sortedQuotes =
      highlights &&
      highlights.slice().sort(function (a, b) {
        return EpubCFI.compare(a.cfi, b.cfi);
      });
    sortedQuotes &&
      rendition &&
      containerRef &&
      containerRef.current &&
      sortedQuotes[0] &&
      rendition.display(sortedQuotes[0].cfi).then(() => {
        gotoQuote(rendition, sortedQuotes[0].cfi);
      });

    setRendition(rendition);
  };

  const highlightClicked = useCallback(
    (clickEvent, highlight) => {
      if (isAnnotatorBarOpen) {
        dispatch(closeAnnotatorBar());
      } else if (containerRef) {
        // ignore click if no containerRef sent, meaning nothing should be done
        const clientRect = clickEvent.target.getBoundingClientRect();
        const parentPos = containerRef.current.getBoundingClientRect();
        const highlightedRect = {
          top: clientRect.top + containerRef.current.scrollTop - parentPos.top,
          left:
            clientRect.left + containerRef.current.scrollLeft - parentPos.left,
          x: clientRect.left + containerRef.current.scrollLeft - parentPos.left,
          y: clientRect.top + containerRef.current.scrollTop - parentPos.top,

          width: clientRect.width,
          height: clientRect.height,
          bottom:
            clientRect.top +
            containerRef.current.scrollTop -
            parentPos.top +
            clientRect.height,
          right:
            clientRect.left +
            containerRef.current.scrollLeft -
            parentPos.left +
            clientRect.width
        };

        dispatch(
          setSelectedHighlight({
            clientRectangle: highlightedRect,
            selectedHighlight: highlight.id,
            isHover: true,
            isClickOnMark: true,
            minimal: highlight.minimal
          })
        );
        dispatch(openAnnotatorBar());
      }
    },
    [dispatch, isAnnotatorBarOpen]
  );

  return (
    <Dialog fullWidth open={dialogOpen} onClose={() => setDialogOpen(false)}>
      <DialogContent
        onScroll={() => {
          if (isPdf) dispatch(closeAnnotatorBar());
        }}
        ref={containerRef}
        className={clsx(
          classes.dialog,
          isPdf && classes.pdfDialog,
          !isPdf && classes.epubDialog
        )}>
        <Box className={classes.actionBarWrapper}>
          <Box className={classes.actionBar}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={navigateToPreviousHighlight}
                className={classes.navigationButton}>
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <Box className={classes.navigationCounter}>
                <Typography variant="body2">
                  {currentHighlightIndex + 1} / {highlights.length} highlights
                </Typography>
              </Box>
              <IconButton
                onClick={navigateToNextHighlight}
                className={classes.navigationButton}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center"
              }}>
              <PdfActionBar contentWidth="800" showPagesControls />
              <Button variant="contained" onClick={() => setDialogOpen(false)}>
                Done
              </Button>
            </Box>
          </Box>
        </Box>
        <Box className={classes.reader}>
          <ReactReaderActions
            id="reactReaderActions"
            minimal={true}
            canComment={false}
            deleteHighlight={removeHighlight}
            container={isPdf ? window.document.body : null}>
            {isPdf ? (
              <PdfView
                url={text.url}
                highlights={highlights}
                highlightClicked={highlightClicked}
                handleTextSelected={onTextSelected}
                isVisible={true}
                scrollToPosition={
                  highlights.length && getLocationByEntity(highlights[0])
                }
              />
            ) : (
              <EpubView
                fullSize={true}
                container={containerRef}
                fontSize={fontSize[fontSizeValue]}
                epubOptions={{ flow: "scrolled-doc" }}
                textDirection={text.text_language === "he" ? "rtl" : "ltr"}
                url={text.url}
                location={text.file_location}
                doNotMove={true}
                highlights={highlights}
                handleTextSelected={onTextSelected}
                onRenditionCreated={onRenditionCreated}
                updateRenditionContext={false}
                readOnly={false}
              />
            )}
          </ReactReaderActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HighlightsDialog;
